import { useEffect, useState, type ReactNode } from 'react';

import { isPaidTier, planSeatsLabel, seatLabel, tierLabel } from '@/admin/admin.plans';
import { useVhAdminStore, VH_ADMIN_SERVICE_TYPE, vhAdminUserServices } from '@/admin/admin.store';
import type {
  VhAdminServiceProvider,
  VhAdminUser,
  VhAdminUserRole,
  VhAdminVessel,
} from '@/admin/admin.types';
import { formatDate, humanize, initials, roleLabel, userContact } from '@/admin/admin.users';
import { AdminBadge } from '@/admin/components/AdminBadge';
import { AdminEntityIdentity } from '@/admin/components/AdminEntityIdentity';
import { AdminSidePanel } from '@/admin/components/AdminSidePanel';
import { AdminSpinner } from '@/admin/components/AdminSpinner';
import styles from '@/admin/pages/UserDetailsPanel.module.scss';
import { imageVariantLink } from '@/common/media';
import { AuthorizedImage } from '@/components/AuthorizedImage';
import { SubscriptionTier } from '@/store/subscriptions/subscriptions.types';
import { toast } from '@/utils/toast';

const PanelTitle = ({ user }: { user: VhAdminUser }) => {
  const { first_name, last_name } = user.person_info;

  return (
    <div className={styles.identity}>
      {user.avatar ? (
        <AuthorizedImage className={styles.avatar} src={imageVariantLink(user.avatar, 40)} alt="" />
      ) : (
        <span className={styles.avatarFallback}>{initials(first_name, last_name)}</span>
      )}
      <span className={styles.name}>
        {first_name} {last_name}
      </span>
    </div>
  );
};

const AccountFacts = ({ user }: { user: VhAdminUser }) => {
  const audiences = useVhAdminStore((state) => state.audiences);
  const audience = audiences.find((item) => item.id === user.beta_group_id);
  const blocked = !!user.block_status && user.block_status !== 'regular';

  return (
    <dl className={styles.facts}>
      <dt>Email</dt>
      <dd>{userContact(user, 'email') ?? '-'}</dd>
      <dt>Phone</dt>
      <dd>{userContact(user, 'phone') ?? '-'}</dd>
      <dt>Status</dt>
      <dd className={styles.badges}>
        <AdminBadge tone={user.activation_status === 'active' ? 'success' : 'neutral'}>
          {humanize(user.activation_status || 'unknown')}
        </AdminBadge>
        {blocked && <AdminBadge tone="danger">{humanize(user.block_status)}</AdminBadge>}
      </dd>
      <dt>Onboarded</dt>
      <dd>{user.onboarded ? 'Yes' : 'No'}</dd>
      <dt>Audience</dt>
      <dd>{audience?.name ?? '-'}</dd>
      <dt>Signed up</dt>
      <dd>{formatDate(user.created_at)}</dd>
    </dl>
  );
};

type VesselMembershipProps = {
  membership: VhAdminUserRole;
  vessel: VhAdminVessel | undefined;
};

const VesselMembership = ({ membership, vessel }: VesselMembershipProps) => {
  const plan = useVhAdminStore((state) => state.vesselPlans[membership.service_id]);
  const tier = plan?.tier ?? vessel?.subscription_tier ?? SubscriptionTier.HARBOR;
  // Only worth a word when billing is not simply running.
  const paymentNote =
    plan && plan.payment_status !== 'active' ? ` · ${humanize(plan.payment_status)}` : '';

  return (
    <li className={styles.membership}>
      <div className={styles.membershipHead}>
        {vessel ? (
          <AdminEntityIdentity
            name={vessel.vessel_name}
            image={vessel.image}
            details={[vessel.make, vessel.model].filter(Boolean).join(' ')}
          />
        ) : (
          <MissingService label="Unknown vessel" id={membership.service_id} />
        )}
        <AdminBadge tone={isPaidTier(tier) ? 'success' : 'neutral'}>
          {tierLabel(tier)}
          {plan?.manually_granted && ' · Lifetime'}
          {paymentNote}
        </AdminBadge>
      </div>

      <dl className={styles.facts}>
        <dt>Role</dt>
        <dd>
          <AdminBadge>{roleLabel(membership.role)}</AdminBadge>
        </dd>
        <dt>Their seat</dt>
        <dd>{seatLabel(membership.seat_type)}</dd>
        <dt>Plan seats</dt>
        <dd>{planSeatsLabel(plan) ?? 'Not limited on Harbor'}</dd>
        <dt>Presence</dt>
        <dd>{membership.presence ? humanize(membership.presence) : '-'}</dd>
        <dt>Joined</dt>
        <dd>{formatDate(membership.join_date)}</dd>
      </dl>
    </li>
  );
};

type ProviderMembershipProps = {
  membership: VhAdminUserRole;
  provider: VhAdminServiceProvider | undefined;
};

/** Providers carry no subscription yet, so there is no plan or seat to show. */
const ProviderMembership = ({ membership, provider }: ProviderMembershipProps) => (
  <li className={styles.membership}>
    <div className={styles.membershipHead}>
      {provider ? (
        <AdminEntityIdentity
          name={provider.name}
          image={provider.logo}
          details={provider.type === 'marina' ? 'Marina' : 'Service provider'}
        />
      ) : (
        <MissingService label="Unknown service provider" id={membership.service_id} />
      )}
    </div>

    <dl className={styles.facts}>
      <dt>Role</dt>
      <dd>
        <AdminBadge>{roleLabel(membership.role)}</AdminBadge>
      </dd>
      <dt>Presence</dt>
      <dd>{membership.presence ? humanize(membership.presence) : '-'}</dd>
      <dt>Joined</dt>
      <dd>{formatDate(membership.join_date)}</dd>
    </dl>
  </li>
);

const MissingService = ({ label, id }: { label: string; id: string }) => (
  <div className={styles.missingService}>
    <span className={styles.name}>{label}</span>
    <span className={styles.muted}>{id}</span>
  </div>
);

type MembershipsSectionProps = {
  title: string;
  empty: string;
  memberships: VhAdminUserRole[];
  renderItem: (membership: VhAdminUserRole) => ReactNode;
};

const MembershipsSection = ({ title, empty, memberships, renderItem }: MembershipsSectionProps) => {
  const loading = useVhAdminStore((state) => state.userServicesLoading);

  return (
    <section className={styles.section}>
      <h3 className={styles.sectionTitle}>
        {title}
        {memberships.length > 0 && <span className={styles.count}>{memberships.length}</span>}
      </h3>
      {memberships.length === 0 ? (
        <p className={styles.muted}>{empty}</p>
      ) : loading ? (
        <div className={styles.loading}>
          <AdminSpinner />
        </div>
      ) : (
        <ul className={styles.memberships}>{memberships.map(renderItem)}</ul>
      )}
    </section>
  );
};

type UserDetailsPanelProps = {
  user: VhAdminUser | null;
  onClose: () => void;
};

export const UserDetailsPanel = ({ user, onClose }: UserDetailsPanelProps) => {
  const fetchUserServices = useVhAdminStore((state) => state.fetchUserServices);
  const userVessels = useVhAdminStore((state) => state.userVessels);
  const userProviders = useVhAdminStore((state) => state.userProviders);
  // Keeps the last person on screen while the panel slides out after a close.
  const [shown, setShown] = useState(user);
  if (user && user !== shown) setShown(user);

  useEffect(() => {
    if (!user) return;
    fetchUserServices(user).catch(() =>
      toast.error("Couldn't load this person's vessels and service providers"),
    );
    // Keyed by id: an audience change hands over a new object for the same person.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  return (
    <AdminSidePanel open={!!user} onClose={onClose} title={shown && <PanelTitle user={shown} />}>
      {shown && (
        <>
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>Account</h3>
            <AccountFacts user={shown} />
          </section>

          <MembershipsSection
            title="Vessels"
            empty="Not on any vessel"
            memberships={vhAdminUserServices(shown, VH_ADMIN_SERVICE_TYPE.VESSEL)}
            renderItem={(membership) => (
              <VesselMembership
                key={membership.service_id}
                membership={membership}
                vessel={userVessels.find((vessel) => vessel.id === membership.service_id)}
              />
            )}
          />

          <MembershipsSection
            title="Service providers"
            empty="Not in any service provider"
            memberships={vhAdminUserServices(shown, VH_ADMIN_SERVICE_TYPE.SERVICE_PROVIDER)}
            renderItem={(membership) => (
              <ProviderMembership
                key={membership.service_id}
                membership={membership}
                provider={userProviders.find((item) => item.id === membership.service_id)}
              />
            )}
          />
        </>
      )}
    </AdminSidePanel>
  );
};
